import type { DatePickerProps, TimePickerProps } from "antd";
import { DatePicker, Dropdown, Select, Space, TimePicker, message } from "antd";
import { useEffect, useState } from "react";
import DownArrow from "../../../assets/DownArrow";
import type { MenuProps } from "antd";
import { Link } from "react-router-dom";
import { format } from "path";
import moment from "moment";
import dayjs from "dayjs";
import { atom, useRecoilState } from "recoil";

const { Option } = Select;
const { RangePicker } = DatePicker;
const defaultValue: [any, any] = [dayjs().subtract(1, "month"), dayjs()];
type PickerType = "time" | "date" | "week";

// Recoil atoms
export const dateRangeState = atom({
  key: "dateRangeState",
  default: defaultValue,
});

export const dateTypeState = atom({
  key: "dateTypeState",
  default: "date" as PickerType,
});

interface PageHeaderProps {
  heading?: string;
  showDropDown?: boolean;
  setDropdownValue?: (value: string) => void; // Adjusted to accept a string
  page?: string;
  onDateRangeChange?: (dateRangeObj: {
    startDate: string | null;
    endDate: string | null;
    dateType: string;
    crawlerType: string;
  }) => void;
  showDateRangeFilter?: boolean;
}
// interface PickerWithOnSelectionChangeProps extends PickerType {
//   onChange: TimePickerProps["onChange"] | DatePickerProps["onChange"];
//   className?: string;
//   // type?: string;
//   onSelectionChange: (
//     startDate: Date | null,
//     endDate: Date | null,
//     type: PickerType
//   ) => void;
// }

const PickerWithType = ({ type, className, onSelectionChange, value }: any) => {
  const dateFormatSelector = (type: string) => {
    let dateFormat: any = "YYYY/MM/DD";
    switch (type) {
      case "date":
        dateFormat = "YYYY/MM/DD";
        break;
      case "month":
        dateFormat = "YYYY/MM";
        break;
      case "quarter":
        dateFormat = null;
        break;
      case "year":
        dateFormat = "YYYY";
        break;
      default:
        return dateFormat;
    }
    return dateFormat;
  };

  const formatValue = dateFormatSelector(type);

  return (
    <RangePicker
      className={className}
      picker={type}
      onChange={(value) => onSelectionChange(value, type)}
      format={formatValue}
      value={value}
      disabledDate={(current) => current && current > dayjs().endOf("day")}
    />
  );
};

const dropDownItems = [
  { label: "Print", path: "print" },
  { label: "Digital", path: "digital" },
  { label: "TV News", path: "tv" },
  { label: "Radio & Podcast", path: "radio" },
];

export default function PageHeader({
  heading = "",
  showDropDown = false,
  setDropdownValue = () => {},
  page = "",
  onDateRangeChange = () => {},
  showDateRangeFilter = true,
}: PageHeaderProps) {
  // const [type, setType] = useState<PickerType>("date");
  // const [dateRange, setDateRange] = useState<[any, any]>(defaultValue);
  const [dateRange, setDateRange] = useRecoilState(dateRangeState);
  const [type, setType] = useRecoilState(dateTypeState);

  const mediaTypeInitial: string =
    localStorage.getItem("mediaType") || "digital";
  const getQuarter = (date: any) => {
    const month = date.month() + 1;
    return Math.ceil(month / 3);
  };

  const handleSelectionChange = (value: any, type: string) => {
    if (value) {
      let [startDate, endDate] = value;
      
      // Ensure 24-hour difference for 'date' type or default
      if ((type === 'date' || !type) && startDate && endDate) {
        const diff = endDate.diff(startDate, 'hour');
        
        if (diff < 24) {
          // Instead of warning, adjust the endDate to be startDate + 24 hours
          endDate = dayjs(startDate).add(23, 'hours').add(59, 'minutes').add(58, 'seconds');
          value = [startDate, endDate];
        }
      }
      
      setDateRange(value);
      let formattedStartDate;
      let formattedEndDate;
      switch (type) {
        case "date":
          formattedStartDate = startDate
            ? startDate.format("YYYY/MM/DD")
            : null;
          formattedEndDate = endDate ? endDate.format("YYYY/MM/DD") : null;
          break;

        case "month":
          formattedStartDate = startDate ? startDate.format("YYYY/MM") : null;
          formattedEndDate = endDate ? endDate.format("YYYY/MM") : null;
          break;
        case "quarter":
          formattedStartDate = startDate
            ? `${startDate.format("YYYY")}-Q${getQuarter(startDate)}`
            : null;
          formattedEndDate = endDate
            ? `${endDate.format("YYYY")}-Q${getQuarter(endDate)}`
            : null;
          break;
        case "year":
          formattedStartDate = startDate ? startDate.format("YYYY") : null;
          formattedEndDate = endDate ? endDate.format("YYYY") : null;
          break;
        default:
          formattedStartDate = startDate
            ? startDate.format("YYYY/MM/DD")
            : null;
          formattedEndDate = endDate ? endDate.format("YYYY/MM/DD") : null;
          break;
      }

      const dateRangeObj = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        dateType: type,
        crawlerType: page,
      };

      onDateRangeChange(dateRangeObj);
    }
  };

  const handleMediaType = (mediaType: string) => {
    localStorage.setItem("mediaType", mediaType);
  };

  const items: MenuProps["items"] = dropDownItems?.map(
    (item: any, index: number) => {
      return {
        key: `${item.label + index}`, // unique id of every dropdown item
        label: (
          <div
            className={`${item?.path === mediaTypeInitial ? "font-bold " : " "}w-full border-b border-[#EBEBEB] text-sm p-3 pl-4 mr-3 `}
            onClick={() => {
              setDropdownValue(item?.path);
              handleMediaType(item?.path);
            }}
          >
            <div className="flex items-center gap-2 ">
              <span className="text-[#404040]">{item?.label}</span>
            </div>
          </div>
        ),
      };
    }
  );

  useEffect(() => {
    setDropdownValue(mediaTypeInitial);
  }, []);

  return (
    <div className="w-full flex md:flex-row flex-col justify-between items-center mb-4 pr-2">
      <div className="flex items-center gap-2 ">
        <span className="md:text-[32px] text-[22px] font-bold text-center text-heading font-poppins ">
          {heading}
        </span>
        {showDropDown && (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            overlayClassName="sidebarProfileDropDown"
          >
            <div className="cursor-pointer active:opacity-50">
              <DownArrow />
            </div>
          </Dropdown>
        )}
      </div>
      {showDateRangeFilter && (
        <div>
          <Space>
            <Select value={type} onChange={setType} className="h-[24px]">
              <Option value="date">Date</Option>
              <Option value="month">Month</Option>
              <Option value="quarter">Quarter</Option>
              <Option value="year">Year</Option>
            </Select>
            <PickerWithType
              type={type}
              className="h-[24px]"
              onSelectionChange={handleSelectionChange}
              value={dateRange}
            />
          </Space>
        </div>
      )}
    </div>
  );
}
